<template>
    <Dialog title="Edit Report" :show="isOpen" :onClose="onClose">
        <el-form :model="item" label-width="120px" label-position="left" size="small">
            <el-form-item label="Name" required>
                <el-input v-model="item.name" />
            </el-form-item>
            <el-form-item label="KPI" required>
                <el-select
                    v-model="item.kpi"
                    placeholder="Select..."
                    filterable
                    :loading="loading.kpis"
                    loading-text="Loading..."
                >
                    <el-option
                        v-for="_kpi in kpis"
                        :key="_kpi.id"
                        :label="_kpi.name"
                        :value="_kpi.id"
                    />
                </el-select>
            </el-form-item>
            <el-form-item label="Chart type" required>
                <el-select v-model="item.chart_type" placeholder="Select..." disabled>
                    <el-option label="Line" value="line" />
                    <el-option label="Pie" value="pie" />
                    <el-option label="Metric" value="metric" />
                </el-select>
            </el-form-item>
            <el-form-item label="Color">
                <el-color-picker v-model="item.border_color" show-alpha />
            </el-form-item>

            <div class="properties-title">
                <h3>Filters</h3>
                <el-button type="primary" size="mini" @click="addFilter" icon="el-icon-plus" round>Add</el-button>
            </div>
            <el-form-item :label="`Property ${index + 1}`" v-for="(f, index) in item.filters" :key="`f_${index}`">
                <el-form inline size="mini" label-position="top">
                    <el-form-item label="Property">
                        <el-select v-model="f.property" placeholder="Property" filterable :loading="loading.properties">
                            <el-option
                                v-for="ep in eventProperties"
                                :key="`f_p_${index}_${ep}`"
                                :label="ep"
                                :value="ep"
                            />
                        </el-select>
                    </el-form-item>
                    <el-form-item label="Operator">
                        <el-select v-model="f.operator" placeholder="Operator">
                            <el-option value="eq" label="=" />
                            <el-option value="neq" label="≠" />
                        </el-select>
                    </el-form-item>
                    <el-form-item label="Value">
                        <el-select v-model="f.value" placeholder="Value" :disabled="!f.property" filterable>
                            <el-option
                                v-for="epv in eventPropertyValues(f.property)"
                                :key="`f_epv_${index}_${epv}`"
                                :label="epv"
                                :value="epv"
                            />
                        </el-select>
                    </el-form-item>
                    <el-form-item label=" ">
                        <el-link type="danger" @click="removeProperty(item.filters, index)">Remove</el-link>
                    </el-form-item>
                </el-form>
            </el-form-item>

            <div class="properties-title">
                <h3>Breakdown</h3>
                <el-button type="primary" size="mini" @click="addBreakdown" icon="el-icon-plus" round>Add</el-button>
            </div>
            <el-form-item :label="`Property ${index + 1}`" v-for="(b, index) in item.breakdown" :key="`b_${index}`">
                <el-form inline size="mini" label-position="top">
                    <el-form-item label="Label">
                        <el-input v-model="b.label" placeholder="Label" />
                    </el-form-item>
                    <el-form-item label="Property">
                        <el-select v-model="b.property" placeholder="Property" filterable :loading="loading.properties">
                            <el-option
                                v-for="ep in eventProperties"
                                :key="`b_p_${index}_${ep}`"
                                :label="ep"
                                :value="ep"
                            />
                        </el-select>
                    </el-form-item>
                    <el-form-item label="Operator">
                        <el-select v-model="b.operator" placeholder="Operator">
                            <el-option value="eq" label="=" />
                            <el-option value="neq" label="≠" />
                        </el-select>
                    </el-form-item>
                    <el-form-item label="Value">
                        <el-select v-model="b.value" placeholder="Value" :disabled="!b.property" filterable>
                            <el-option
                                v-for="epv in eventPropertyValues(b.property)"
                                :key="`b_epv_${index}_${epv}`"
                                :label="epv"
                                :value="epv"
                            />
                        </el-select>
                    </el-form-item>
                    <el-form-item label="Color">
                        <el-color-picker v-model="b.border_color" show-alpha />
                    </el-form-item>
                    <el-form-item label=" ">
                        <el-link type="danger" @click="removeProperty(item.breakdown, index)">Remove</el-link>
                    </el-form-item>
                </el-form>
            </el-form-item>

            <el-form-item>
                <el-button type="primary" @click="save">{{ item.id ? 'Save' : 'Create' }}</el-button>
                <el-button @click="onClose()">Cancel</el-button>
            </el-form-item>
        </el-form>
    </Dialog>
</template>

<script>
import { cloneDeep, isEmpty } from 'lodash';
import { mapGetters, mapActions } from 'vuex';
import Dialog from '@/components/Dialog';
import { defaultFilterItem ,defaultBreakdownItem } from '../constants';
import { getRandomColor } from '@/utils/colors';

export default {
    data() {
        return {
            gutter: 10,
            inputSpan: 6,
            actionSpan: 4
        };
    },
    props: {
        item: Object,
        isOpen: Boolean,
        onClose: Function,
    },
    components: {
        Dialog,
    },
    mounted() {
        if (isEmpty(this.kpis)) this.getKpis();
        if (isEmpty(this.eventProperties)) this.getEventProperties();
    },
    computed: {
        ...mapGetters(['kpis', 'eventProperties', 'eventPropertyValues', 'loading']),
    },
    methods: {
        ...mapActions(['getReports', 'getEventProperties', 'getKpis']),
        addFilter() {
            const f = cloneDeep(defaultFilterItem);
            this.item.filters.push(f);
        },
        addBreakdown() {
            const b = cloneDeep(defaultBreakdownItem);
            b.border_color = getRandomColor();
            this.item.breakdown.push(b);
        },
        removeProperty(properties, index) {
            properties.splice(index, 1);
        },
        isArrayValid(arr) {
            return arr.every(item => item.property && item.value && item.operator);
        },
        async save() {
            try {
                const { isArrayValid } = this;
                const { id, name, kpi, filters, breakdown } = this.item;

                if (!name || !kpi) {
                    return this.$message.error('Please fill report name and KPI');
                }
                if (!isArrayValid(filters) || !isArrayValid(breakdown)) {
                    return this.$message.error('Please fill all filter and breakdown fields');
                }

                await this.$api({
                    url: `/reports/${id || ''}`,
                    method: id ? 'put' : 'post',
                    data: {
                        ...this.item,
                    }
                });

                this.$message.success(`${id ? 'Saved' : 'Created'} successfully`);
                this.onClose();
            } catch (e) {
                this.$notify.error({ title: 'Failed saving report', message: e.message, });
            } finally {
                this.getReports();
            }
        }
    }
}
</script>

<style scoped>
.properties-title {
    display: flex;
    align-items: center;
    justify-content: space-between;
}
.properties-title > h3 {
    margin-right: 5px;
}
.column-header {
    font-weight: bold;
    margin-left: 5px;
}
</style>
